import { useEffect, useMemo, useRef, useState } from "react"
import { BarChart3, LineChart, PieChart, X } from "lucide-react"
import type { EChartsOption } from "echarts"
import { ColorInput } from "@/components/ui/color-input"
import { EChartView } from "@/components/ui/echart-view"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

interface ChartTabProps {
  data: Record<string, unknown>[] | undefined
  onConfigChange?: (config: { chartType: ChartType; xAxis: string; yAxis: string }) => void
  initialConfig?: { chartType?: ChartType; xAxis?: string; yAxis?: string }
}

type ChartType = "bar" | "line" | "pie"

type LegendPosition = "top" | "top-left" | "top-right" | "bottom" | "bottom-left" | "bottom-right" | "left" | "right"

export function ChartTab({ data, onConfigChange, initialConfig }: ChartTabProps) {
  const [chartType, setChartType] = useState<ChartType>(initialConfig?.chartType || "bar")
  const [chartXAxis, setChartXAxis] = useState(initialConfig?.xAxis || "")
  const [chartYAxis, setChartYAxis] = useState(initialConfig?.yAxis || "")
  const [optionsTab, setOptionsTab] = useState<"data" | "appearance" | "axes" | "series">("data")

  // Data / series (existing + extended)
  const [chartCumulative, setChartCumulative] = useState(false)
  const [chartShowLabels, setChartShowLabels] = useState(false)
  const [chartShowGrid, setChartShowGrid] = useState(false)
  const [chartOrientation, setChartOrientation] = useState<"vertical" | "horizontal">("vertical")

  // Appearance (all charts) – https://echarts.apache.org/en/option.html#title, #backgroundColor, #legend, #tooltip, #color
  const [titleShow, setTitleShow] = useState(false)
  const [titleText, setTitleText] = useState("")
  const [titleSubtext, setTitleSubtext] = useState("")
  const [titleFontSize, setTitleFontSize] = useState(18)
  const [titleColor, setTitleColor] = useState("#333")
  const [backgroundColor, setBackgroundColor] = useState("")
  const [legendShow, setLegendShow] = useState(true)
  const [legendOrient, setLegendOrient] = useState<"horizontal" | "vertical">("vertical")
  const [legendPosition, setLegendPosition] = useState<LegendPosition>("bottom")
  const [legendTextColor, setLegendTextColor] = useState("#333")
  const [tooltipShow, setTooltipShow] = useState(true)
  const [tooltipBg, setTooltipBg] = useState("rgba(255,255,255,0.96)")
  const [tooltipBorderColor, setTooltipBorderColor] = useState("#ccc")
  const [tooltipTextColor, setTooltipTextColor] = useState("#333")
  const [tooltipExtraColumns, setTooltipExtraColumns] = useState<string[]>([])
  const [colorPalette, setColorPalette] = useState("")

  // Grid & axes (bar/line) – https://echarts.apache.org/en/option.html#grid, #xAxis, #yAxis
  const [gridLeft, setGridLeft] = useState("10")
  const [gridRight, setGridRight] = useState("10")
  const [gridTop, setGridTop] = useState("15")
  const [gridBottom, setGridBottom] = useState("15")
  const [xAxisName, setXAxisName] = useState("")
  const [yAxisName, setYAxisName] = useState("")
  const [xAxisTickMode, setXAxisTickMode] = useState<"hide" | "show" | "compact" | "rotate45" | "rotate90">("rotate45")
  const [yAxisTickMode, setYAxisTickMode] = useState<"hide" | "show" | "compact" | "rotate45" | "rotate90">("show")
  const [xAxisLabelFontSize, setXAxisLabelFontSize] = useState(12)
  const [yAxisLabelFontSize, setYAxisLabelFontSize] = useState(12)
  const [xAxisLabelColor, setXAxisLabelColor] = useState("#333")
  const [yAxisLabelColor, setYAxisLabelColor] = useState("#333")
  const [axisLineColor, setAxisLineColor] = useState("#333")
  const [splitLineColor, setSplitLineColor] = useState("#eee")

  // Bar – https://echarts.apache.org/en/option.html#series-bar
  const [barColor, setBarColor] = useState("")
  const [barWidth, setBarWidth] = useState("60")
  const [barGap, setBarGap] = useState("30")
  const [barBorderColor, setBarBorderColor] = useState("")
  const [barBorderWidth, setBarBorderWidth] = useState(0)
  const [barBorderRadius, setBarBorderRadius] = useState(0)

  // Line – https://echarts.apache.org/en/option.html#series-line
  const [lineColor, setLineColor] = useState("")
  const [lineSmooth, setLineSmooth] = useState(false)
  const [lineShowSymbol, setLineShowSymbol] = useState(true)
  const [lineSymbolSize, setLineSymbolSize] = useState(4)
  const [lineWidth, setLineWidth] = useState(2)
  const [lineAreaShow, setLineAreaShow] = useState(false)
  const [lineAreaOpacity, setLineAreaOpacity] = useState(0.3)

  // Pie – https://echarts.apache.org/en/option.html#series-pie
  const [pieSliceColors, setPieSliceColors] = useState<Record<string, string>>({})
  const [pieRadiusInner, setPieRadiusInner] = useState("0%")
  const [pieRadiusOuter, setPieRadiusOuter] = useState("75%")
  const [pieCenterX, setPieCenterX] = useState("50%")
  const [pieCenterY, setPieCenterY] = useState("50%")
  const [pieRoseType, setPieRoseType] = useState<"radius" | "area" | "">("")
  const [pieLabelLineShow, setPieLabelLineShow] = useState(true)
  const [pieItemBorderColor, setPieItemBorderColor] = useState("#fff")
  const [pieItemBorderWidth, setPieItemBorderWidth] = useState(1)

  const chartColumns = Object.keys(data?.[0] ?? {})

  // Y Axis / Value: only columns whose values are number or integer
  const yAxisColumns = useMemo(() => {
    const cols = Object.keys(data?.[0] ?? {})
    if (!data || data.length === 0) return cols
    const out: string[] = []
    for (const col of cols) {
      let hasNumeric = false
      let allNumericOrEmpty = true
      for (const row of data) {
        const v = row[col]
        if (v == null || v === "") continue
        if (typeof v === "number" && !isNaN(v)) {
          hasNumeric = true
          continue
        }
        if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) {
          hasNumeric = true
          continue
        }
        allNumericOrEmpty = false
        break
      }
      if (hasNumeric && allNumericOrEmpty) out.push(col)
    }
    return out
  }, [data])

  const pieSliceNames: string[] = []
  if (data && chartXAxis) {
    const seen = new Set<string>()
    for (const row of data) {
      const n = String(row[chartXAxis] ?? "")
      if (n && !seen.has(n)) {
        seen.add(n)
        pieSliceNames.push(n)
      }
    }
  }

  useEffect(() => {
    const cols = Object.keys(data?.[0] ?? {})
    if (chartXAxis && !cols.includes(chartXAxis)) setChartXAxis("")
    // Y axis must be a numeric column
    if (chartYAxis && !yAxisColumns.includes(chartYAxis)) setChartYAxis("")
    setTooltipExtraColumns((prev) => prev.filter((c) => cols.includes(c)))
  }, [data, chartXAxis, chartYAxis, yAxisColumns])

  useEffect(() => {
    if (chartType === "pie" && optionsTab === "axes") setOptionsTab("series")
  }, [chartType, optionsTab])

  // Sync initialConfig when provided (for widget editing). Only sync when values differ to avoid loops.
  const lastInitialConfigRef = useRef<{ chartType: ChartType; xAxis: string; yAxis: string } | null>(null)
  useEffect(() => {
    if (!initialConfig) return
    const next = {
      chartType: (initialConfig.chartType || "bar") as ChartType,
      xAxis: initialConfig.xAxis || "",
      yAxis: initialConfig.yAxis || "",
    }
    const prev = lastInitialConfigRef.current
    if (prev && prev.chartType === next.chartType && prev.xAxis === next.xAxis && prev.yAxis === next.yAxis) return
    lastInitialConfigRef.current = next
    if (next.chartType !== chartType) setChartType(next.chartType)
    if (next.xAxis !== chartXAxis) setChartXAxis(next.xAxis)
    if (next.yAxis !== chartYAxis) setChartYAxis(next.yAxis)
  }, [initialConfig, chartType, chartXAxis, chartYAxis])

  // Per-chart-type defaults for Show labels and Legend: pie = labels on, legend off; bar/line = labels off, legend on.
  // For pie: default legend orient = Vertical, position = Left (or Bottom when orient is Horizontal).
  useEffect(() => {
    if (chartType === "pie") {
      setChartShowLabels(true)
      setLegendShow(false)
      setLegendOrient("vertical")
      setLegendPosition("left")
    } else {
      setChartShowLabels(false)
      setLegendShow(false)
    }
  }, [chartType])

  // Notify parent only when config actually changes (avoid loop: parent update → initialConfig → sync → notify → parent update).
  const lastNotifiedRef = useRef<{ chartType: ChartType; xAxis: string; yAxis: string } | null>(null)
  useEffect(() => {
    if (!onConfigChange) return
    const next = { chartType, xAxis: chartXAxis, yAxis: chartYAxis }
    const prev = lastNotifiedRef.current
    if (prev && prev.chartType === next.chartType && prev.xAxis === next.xAxis && prev.yAxis === next.yAxis) return
    lastNotifiedRef.current = next
    onConfigChange(next)
  }, [chartType, chartXAxis, chartYAxis, onConfigChange])

  function parseColors(str: string): string[] | undefined {
    const arr = str
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    return arr.length > 0 ? arr : undefined
  }

  function formatTooltipValue(v: unknown): string {
    if (v == null || v === "") return "—"
    if (typeof v === "number" && !isNaN(v)) return v.toLocaleString()
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      const d = new Date(v)
      if (!isNaN(d.getTime())) return d.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })
    }
    return String(v)
  }

  function buildChartOption(): EChartsOption | null {
    if (!data || !Array.isArray(data) || data.length === 0 || !chartXAxis || !chartYAxis) return null
    const xData = data.map((r) => String(r[chartXAxis] ?? ""))
    const yData = data.map((r) => Number(r[chartYAxis]) || 0)
    const colors = parseColors(colorPalette)

    const base: EChartsOption = {
      ...(colors && { color: colors }),
      ...(backgroundColor && { backgroundColor }),
      ...(titleShow &&
        (titleText || titleSubtext) && {
          title: {
            text: titleText || undefined,
            subtext: titleSubtext || undefined,
            left: "center",
            top: 10,
            textStyle: { fontSize: titleFontSize, color: titleColor },
            subtextStyle: { fontSize: titleFontSize - 2, color: titleColor },
          },
        }),
      legend: (() => {
        if (!legendShow) return { show: false }
        const isHorz = legendOrient === "horizontal"
        // Position from legendPosition + legendOrient. ECharts: left/right/top/bottom; avoid setting both left+right or top+bottom.
        const pos = ((): { left?: string; right?: string; top?: string; bottom?: string } => {
          if (isHorz) {
            switch (legendPosition) {
              case "top":
                return { left: "8%", right: "8%", top: "2%" }
              case "top-left":
                return { left: "2%", top: "2%" }
              case "top-right":
                return { right: "2%", top: "2%" }
              case "bottom":
                return { left: "8%", right: "8%", bottom: "10%" }
              case "bottom-left":
                return { left: "2%", bottom: "10%" }
              case "bottom-right":
                return { right: "2%", bottom: "10%" }
              case "left":
                return { left: "2%", top: "center" }
              case "right":
                return { right: "2%", top: "center" }
              default:
                return { left: "8%", right: "8%", bottom: "10%" }
            }
          }
          switch (legendPosition) {
            case "top":
              return { left: "42%", top: "2%" }
            case "top-left":
              return { left: "2%", top: "2%" }
            case "top-right":
              return { right: "2%", top: "2%" }
            case "bottom":
              return { left: "42%", bottom: "10%" }
            case "bottom-left":
              return { left: "2%", bottom: "10%" }
            case "bottom-right":
              return { right: "2%", bottom: "10%" }
            case "left":
              return { left: "2%", bottom: "10%" }
            case "right":
              return { right: "2%", bottom: "10%" }
            default:
              return { left: "2%", bottom: "10%" }
          }
        })()
        return {
          type: "scroll",
          show: true,
          orient: isHorz ? "horizontal" : "vertical",
          ...(isHorz ? { height: 50 } : { width: "16%" }),
          ...pos,
          textStyle: { color: legendTextColor },
        }
      })(),
      tooltip: tooltipShow
        ? {
            show: true,
            trigger: chartType === "pie" ? "item" : "axis",
            backgroundColor: tooltipBg || undefined,
            borderColor: tooltipBorderColor || undefined,
            textStyle: { color: tooltipTextColor },
            ...((chartType === "pie" || tooltipExtraColumns.length > 0) && {
              formatter: (params: unknown) => {
                const isAxis = chartType !== "pie"
                const parr = isAxis
                  ? (params as {
                      dataIndex?: number
                      axisValue?: string
                      seriesName?: string
                      value?: number
                      color?: string
                    }[])
                  : [params as { dataIndex?: number; name?: string; value?: number; percent?: number; color?: string }]
                const p = parr[0]
                if (!p) return ""
                if (isAxis && p.dataIndex == null) return ""
                const row = p.dataIndex != null ? (data[p.dataIndex] as Record<string, unknown> | undefined) : undefined
                const lines: string[] = []
                const valueLabel = chartYAxis || "Value"
                if (isAxis) {
                  const ap = p as { axisValue?: string }
                  lines.push(ap.axisValue != null ? String(ap.axisValue) : "")
                  for (const x of parr) {
                    const v = x.value != null ? formatTooltipValue(x.value) : "—"
                    const color = (x as { color?: string }).color ?? "#333"
                    lines.push(
                      `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:4px;vertical-align:middle"></span>${valueLabel}: ${v}`
                    )
                  }
                } else {
                  lines.push((p as { name?: string }).name != null ? String((p as { name?: string }).name) : "")
                  const color = (p as { color?: string }).color ?? "#333"
                  const val = Number(p.value) || 0
                  let percent: number
                  const echartsPercent = (p as { percent?: number }).percent
                  if (typeof echartsPercent === "number" && !isNaN(echartsPercent)) {
                    percent = echartsPercent
                  } else {
                    const total = data.reduce((sum, r) => sum + (Number(r[chartYAxis]) || 0), 0)
                    percent = total > 0 ? (val / total) * 100 : 0
                  }
                  lines.push(
                    `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:4px;vertical-align:middle"></span>${valueLabel}: ${formatTooltipValue(p.value)} (${percent.toFixed(1)}%)`
                  )
                }
                if (row && tooltipExtraColumns.length > 0) {
                  for (const col of tooltipExtraColumns) {
                    lines.push(`${col}: ${formatTooltipValue(row[col])}`)
                  }
                }
                return lines.filter(Boolean).join("<br/>")
              },
            }),
          }
        : { show: false },
    }

    if (chartType === "pie") {
      const radius: [string, string] | string =
        pieRadiusInner && pieRadiusInner !== "0%" ? [pieRadiusInner, pieRadiusOuter] : pieRadiusOuter
      // When legend is shown, place pie so it never overlaps. Horizontal legend has fixed
      // height at bottom → pie higher and smaller; vertical has fixed width on left → pie right.
      const pieCenter: [string, string] = legendShow
        ? legendOrient === "horizontal"
          ? ["50%", "38%"]
          : ["62%", "50%"]
        : [pieCenterX, pieCenterY]
      const pieRadius: [string, string] | string = legendShow
        ? pieRadiusInner && pieRadiusInner !== "0%"
          ? legendOrient === "horizontal"
            ? [pieRadiusInner, "32%"]
            : [pieRadiusInner, "35%"]
          : legendOrient === "horizontal"
            ? "32%"
            : "35%"
        : radius
      return {
        ...base,
        series: [
          {
            type: "pie",
            name: chartYAxis || "Value",
            radius: pieRadius,
            center: pieCenter,
            ...(pieRoseType && { roseType: pieRoseType }),
            data: data.map((r) => {
              const name = String(r[chartXAxis] ?? "")
              const value = Number(r[chartYAxis]) || 0
              const customColor = pieSliceColors[name]
              return {
                name,
                value,
                ...(customColor && { itemStyle: { color: customColor } }),
              }
            }),
            label: { show: chartShowLabels },
            labelLine: { show: pieLabelLineShow },
            itemStyle: {
              borderColor: pieItemBorderColor || undefined,
              borderWidth: pieItemBorderWidth,
            },
          },
        ],
      } as EChartsOption
    }

    // Bar / Line
    const isBar = chartType === "bar"
    // For bar: when we have a palette and no barColor, give each bar a color from the palette (cycle by index)
    const barData =
      isBar && colors && !barColor
        ? yData.map((val, i) => ({ value: val, itemStyle: { color: colors[i % colors.length] } }))
        : yData
    const series: EChartsOption["series"] = [
      {
        type: chartType,
        name: chartYAxis || "Value",
        data: isBar ? barData : yData,
        stack: chartCumulative ? "total" : undefined,
        label: { show: chartShowLabels },
        ...(isBar && {
          barWidth: barWidth ? `${String(barWidth).replace(/%/g, "")}%` : undefined,
          barGap: barGap ? `${String(barGap).replace(/%/g, "")}%` : undefined,
          itemStyle: {
            ...(barColor && { color: barColor }),
            ...(barBorderColor && { borderColor: barBorderColor }),
            ...(barBorderWidth > 0 && { borderWidth: barBorderWidth }),
            ...(barBorderRadius > 0 && { borderRadius: barBorderRadius }),
          },
        }),
        ...(!isBar && {
          smooth: lineSmooth,
          symbol: lineShowSymbol ? "circle" : "none",
          symbolSize: lineSymbolSize,
          lineStyle: { width: lineWidth, ...(lineColor && { color: lineColor }) },
          ...(lineColor && { itemStyle: { color: lineColor } }),
          ...(lineAreaShow && { areaStyle: { opacity: lineAreaOpacity } }),
        }),
      },
    ]

    // When legend is shown, reserve space so plot doesn't overlap (horizontal=bottom → more grid.bottom; vertical=left → more grid.left)
    const pct = (s: string, d: number) => {
      const n = parseInt(String(s).replace(/%/g, ""), 10)
      return isNaN(n) || n < 0 ? d : n
    }
    const gridCfg = {
      left: legendShow && legendOrient === "vertical" ? "20%" : `${pct(gridLeft, 10)}%`,
      right: `${pct(gridRight, 10)}%`,
      top: `${pct(gridTop, 15)}%`,
      bottom: legendShow && legendOrient === "horizontal" ? "28%" : `${pct(gridBottom, 15)}%`,
      containLabel: true,
    }
    // X-axis lines, ticks, and label rotation from "Show lines and tick marks (X axis)" mode
    const xTick = (() => {
      switch (xAxisTickMode) {
        case "hide":
          return { line: false, tick: false, rotate: 0, fontSize: xAxisLabelFontSize }
        case "show":
          return { line: true, tick: true, rotate: 0, fontSize: xAxisLabelFontSize }
        case "compact":
          return { line: true, tick: true, rotate: 0, fontSize: 9 }
        case "rotate45":
          return { line: true, tick: true, rotate: 45, fontSize: xAxisLabelFontSize }
        case "rotate90":
          return { line: true, tick: true, rotate: 90, fontSize: xAxisLabelFontSize }
        default:
          return { line: true, tick: true, rotate: 0, fontSize: xAxisLabelFontSize }
      }
    })()
    // Y-axis lines, ticks, and label rotation from "Show lines and tick marks (Y axis)" mode
    const yTick = (() => {
      switch (yAxisTickMode) {
        case "hide":
          return { line: false, tick: false, rotate: 0, fontSize: yAxisLabelFontSize }
        case "show":
          return { line: true, tick: true, rotate: 0, fontSize: yAxisLabelFontSize }
        case "compact":
          return { line: true, tick: true, rotate: 0, fontSize: 9 }
        case "rotate45":
          return { line: true, tick: true, rotate: 45, fontSize: yAxisLabelFontSize }
        case "rotate90":
          return { line: true, tick: true, rotate: 90, fontSize: yAxisLabelFontSize }
        default:
          return { line: true, tick: true, rotate: 0, fontSize: yAxisLabelFontSize }
      }
    })()
    // category = X axis labels (xData); value = Y axis scale. For horizontal bars, swap which physical axis each uses.
    const categoryAxis = {
      type: "category" as const,
      data: xData,
      name: xAxisName || undefined,
      nameLocation: "middle" as const,
      nameGap: 30,
      axisLine: { show: xTick.line, lineStyle: { color: axisLineColor } },
      axisTick: xAxisTickMode === "hide" ? { show: false, length: 0 } : { show: xTick.tick },
      axisLabel: {
        show: xAxisTickMode !== "hide",
        rotate: xTick.rotate,
        fontSize: xTick.fontSize,
        color: xAxisLabelColor,
      },
      splitLine: { show: chartShowGrid, lineStyle: { color: splitLineColor, type: "dashed" } },
    }
    const valueAxis = {
      type: "value" as const,
      name: yAxisName || undefined,
      axisLine: { show: yTick.line, lineStyle: { color: axisLineColor } },
      axisTick: yAxisTickMode === "hide" ? { show: false, length: 0 } : { show: yTick.tick },
      axisLabel: {
        show: yAxisTickMode !== "hide",
        rotate: yTick.rotate,
        fontSize: yTick.fontSize,
        color: yAxisLabelColor,
      },
      splitLine: { show: chartShowGrid, lineStyle: { color: splitLineColor, type: "dashed" } },
    }
    const isHorizontal = chartOrientation === "horizontal"
    return {
      ...base,
      grid: gridCfg,
      xAxis: isHorizontal ? { ...valueAxis, boundaryGap: [0, 0.01] } : categoryAxis,
      yAxis: isHorizontal ? categoryAxis : valueAxis,
      series,
    } as EChartsOption
  }

  const opt = buildChartOption()

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-3 md:flex-row">
      {/* Chart area */}
      <div className="min-h-[400px] min-w-0 flex-1 md:h-auto md:min-h-0">
        {opt ? (
          <EChartView option={opt} height="100%" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 px-4 dark:border-gray-700">
            <BarChart3 className="mb-2 h-10 w-10 text-gray-400 dark:text-gray-500" />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {!(data && data.length > 0) ? "No data to show" : "Select axes to see the chart"}
            </p>
            <p className="mt-1 text-center text-xs text-gray-400 dark:text-gray-500">
              {!(data && data.length > 0)
                ? "Run a query to load data, then select X and Y axis in the Data tab."
                : chartType === "pie"
                  ? "Select category and value in the Data tab."
                  : "Select your X and Y axis in the Data tab."}
            </p>
          </div>
        )}
      </div>
      {/* Chart options */}
      <div className="flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12] md:w-72">
        <h3 className="shrink-0 border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-900 dark:border-gray-800 dark:text-white">
          Chart options
        </h3>
        <Tabs
          value={optionsTab}
          onValueChange={(v) => setOptionsTab(v as typeof optionsTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList className="mx-2 mt-2 flex h-auto shrink-0 flex-nowrap gap-0.5 overflow-x-auto bg-gray-100 p-1 dark:bg-gray-800">
            <TabsTrigger
              value="data"
              className="h-7 min-w-0 flex-1 whitespace-nowrap text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
            >
              Data
            </TabsTrigger>
            <TabsTrigger
              value="appearance"
              className="h-7 min-w-0 flex-1 whitespace-nowrap text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
            >
              Appearance
            </TabsTrigger>
            <TabsTrigger
              value="axes"
              className={cn(
                "h-7 min-w-0 flex-1 whitespace-nowrap text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700",
                chartType === "pie" && "hidden"
              )}
            >
              Axes
            </TabsTrigger>
            <TabsTrigger
              value="series"
              className="h-7 min-w-0 flex-1 whitespace-nowrap text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
            >
              Series
            </TabsTrigger>
          </TabsList>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {/* Data */}
            <TabsContent value="data" className="m-0 space-y-3">
              {(!(data && data.length > 0) || !chartXAxis || !chartYAxis) && (
                <p className="rounded-md bg-gray-50 px-2 py-1.5 text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {!(data && data.length > 0)
                    ? "Run a query to load data, then select X and Y axis below."
                    : chartType === "pie"
                      ? "Select category and value below to display the chart."
                      : "Select X and Y axis below to display the chart."}
                </p>
              )}
              <div>
                <Label className="mb-1 block text-[10px]">Chart type</Label>
                <div className="flex gap-1">
                  {(["bar", "line", "pie"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setChartType(t)}
                      className={cn(
                        "flex flex-1 flex-col items-center gap-0.5 rounded border p-1.5 text-[10px] transition-colors",
                        chartType === t
                          ? "border-gray-900 bg-gray-100 text-gray-900 dark:border-white dark:bg-gray-800 dark:text-white"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                      )}
                    >
                      {t === "bar" && <BarChart3 className="h-4 w-4" />}
                      {t === "line" && <LineChart className="h-4 w-4" />}
                      {t === "pie" && <PieChart className="h-4 w-4" />}
                      <span className="capitalize">{t}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-[10px]">
                  {chartType === "pie" ? "Category (slice labels)" : "X Axis"}
                </Label>
                <Select
                  value={chartXAxis || "__none__"}
                  onValueChange={(v) => setChartXAxis(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger className="h-7 text-[10px]">
                    <SelectValue placeholder={chartType === "pie" ? "Select category" : "Select X Axis"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-xs">
                      {chartType === "pie" ? "Select category" : "Select X Axis"}
                    </SelectItem>
                    {chartColumns.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-[10px]">
                  {chartType === "pie" ? "Value (slice size)" : "Y Axis"}
                </Label>
                <Select
                  value={chartYAxis || "__none__"}
                  onValueChange={(v) => setChartYAxis(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger className="h-7 text-[10px]">
                    <SelectValue placeholder={chartType === "pie" ? "Select value" : "Select Y Axis"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-xs">
                      {chartType === "pie" ? "Select value" : "Select Y Axis"}
                    </SelectItem>
                    {yAxisColumns.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* Appearance – title, background, legend, tooltip, color palette */}
            <TabsContent value="appearance" className="m-0 space-y-3">
              {chartType !== "pie" && (
                <div>
                  <Label className="mb-1 block text-[10px]">Chart orientation</Label>
                  <Select
                    value={chartOrientation}
                    onValueChange={(v) => setChartOrientation(v as "vertical" | "horizontal")}
                  >
                    <SelectTrigger className="h-7 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical" className="text-xs">
                        Vertical
                      </SelectItem>
                      <SelectItem value="horizontal" className="text-xs">
                        Horizontal
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-[10px]">Title</Label>
                <Switch checked={titleShow} onCheckedChange={setTitleShow} />
              </div>
              {titleShow && (
                <>
                  <Input
                    className="h-7 text-[10px]"
                    placeholder="Title"
                    value={titleText}
                    onChange={(e) => setTitleText(e.target.value)}
                  />
                  <Input
                    className="h-7 text-[10px]"
                    placeholder="Subtitle"
                    value={titleSubtext}
                    onChange={(e) => setTitleSubtext(e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <Label className="mb-0.5 block text-[10px]">Title font size</Label>
                      <Input
                        type="number"
                        className="h-7 text-[10px]"
                        value={titleFontSize}
                        onChange={(e) => setTitleFontSize(Number(e.target.value) || 18)}
                      />
                    </div>
                    <div>
                      <Label className="mb-0.5 block text-[10px]">Title color</Label>
                      <ColorInput placeholder="#333" value={titleColor} onChange={setTitleColor} />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className="mb-1 block text-[10px]">Background</Label>
                <ColorInput
                  placeholder="transparent, #fff, rgba(...)"
                  value={backgroundColor}
                  onChange={setBackgroundColor}
                />
              </div>
              <div>
                <Label className="mb-1 block text-[10px]">Color palette</Label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(parseColors(colorPalette) || []).map((c, i) => (
                    <div key={`${c}-${i}`} className="group relative">
                      <ColorInput
                        swatchOnly
                        className="shrink-0"
                        value={c}
                        onChange={(newC) => {
                          const arr = [...(parseColors(colorPalette) || [])]
                          if (i < arr.length) {
                            arr[i] = newC
                            setColorPalette(arr.join(", "))
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          const arr = (parseColors(colorPalette) || []).filter((_, j) => j !== i)
                          setColorPalette(arr.join(", "))
                        }}
                        className="absolute -right-1 -top-1 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 opacity-0 shadow-sm transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-red-950 dark:hover:text-red-400"
                        aria-label={`Remove ${c}`}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Add</span>
                    <ColorInput
                      swatchOnly
                      value=""
                      onChange={(c) => setColorPalette((prev) => (prev ? `${prev}, ${c}`.trim() : c))}
                    />
                  </div>
                </div>
                <Input
                  className="mt-1 h-7 text-[10px]"
                  placeholder="#5470c6, #91cc75, #fac858 (or paste)"
                  value={colorPalette}
                  onChange={(e) => setColorPalette(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px]">Show labels</Label>
                <Switch checked={chartShowLabels} onCheckedChange={setChartShowLabels} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px]">Legend</Label>
                <Switch checked={legendShow} onCheckedChange={setLegendShow} />
              </div>
              {legendShow && (
                <>
                  <Select value={legendOrient} onValueChange={(v) => setLegendOrient(v as "horizontal" | "vertical")}>
                    <SelectTrigger className="h-7 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="horizontal" className="text-xs">
                        Horizontal
                      </SelectItem>
                      <SelectItem value="vertical" className="text-xs">
                        Vertical
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Position</Label>
                    <Select value={legendPosition} onValueChange={(v) => setLegendPosition(v as LegendPosition)}>
                      <SelectTrigger className="h-7 text-[10px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top" className="text-xs">
                          Top
                        </SelectItem>
                        <SelectItem value="top-left" className="text-xs">
                          Top left
                        </SelectItem>
                        <SelectItem value="top-right" className="text-xs">
                          Top right
                        </SelectItem>
                        <SelectItem value="bottom" className="text-xs">
                          Bottom
                        </SelectItem>
                        <SelectItem value="bottom-left" className="text-xs">
                          Bottom left
                        </SelectItem>
                        <SelectItem value="bottom-right" className="text-xs">
                          Bottom right
                        </SelectItem>
                        <SelectItem value="left" className="text-xs">
                          Left
                        </SelectItem>
                        <SelectItem value="right" className="text-xs">
                          Right
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Legend text color</Label>
                    <ColorInput placeholder="#333" value={legendTextColor} onChange={setLegendTextColor} />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-[10px]">Tooltip</Label>
                <Switch checked={tooltipShow} onCheckedChange={setTooltipShow} />
              </div>
              {tooltipShow && (
                <>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Tooltip bg</Label>
                    <ColorInput value={tooltipBg} onChange={setTooltipBg} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Tooltip border</Label>
                    <ColorInput value={tooltipBorderColor} onChange={setTooltipBorderColor} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Tooltip text color</Label>
                    <ColorInput value={tooltipTextColor} onChange={setTooltipTextColor} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Additional tooltip columns</Label>
                    <div className="flex flex-wrap gap-1 rounded-md border border-gray-200 bg-white p-1.5 dark:border-gray-700 dark:bg-gray-900">
                      {tooltipExtraColumns.map((col) => (
                        <span
                          key={col}
                          className="text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 inline-flex items-center gap-1 rounded bg-primary/5 px-1.5 py-0.5 text-[10px]"
                        >
                          {col}
                          <button
                            type="button"
                            onClick={() => setTooltipExtraColumns((prev) => prev.filter((c) => c !== col))}
                            className="dark:hover:bg-primary-800/50 rounded p-0.5 hover:bg-primary/10"
                            aria-label={`Remove ${col}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <Select
                        value="__add__"
                        onValueChange={(v) => {
                          if (v && v !== "__add__")
                            setTooltipExtraColumns((prev) => (prev.includes(v) ? prev : [...prev, v]))
                        }}
                      >
                        <SelectTrigger className="h-6 min-w-[100px] border-0 bg-transparent text-xs shadow-none focus:ring-0 focus-visible:ring-0 dark:bg-transparent">
                          <SelectValue placeholder="Add column..." />
                        </SelectTrigger>
                        <SelectContent className="text-xs">
                          <SelectItem value="__add__" className="text-xs text-gray-500">
                            Add column...
                          </SelectItem>
                          {chartColumns
                            .filter((c) => !tooltipExtraColumns.includes(c))
                            .map((c) => (
                              <SelectItem key={c} value={c} className="text-xs">
                                {c}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Axes – bar/line only */}
            <TabsContent value="axes" className="m-0 space-y-3">
              <div>
                <Label className="mb-0.5 block text-[10px]">Grid (left, right, top, bottom)</Label>
                <div className="grid grid-cols-2 gap-1">
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      className="h-7 pr-6 text-[10px]"
                      placeholder="left"
                      value={gridLeft}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "") {
                          setGridLeft("")
                          return
                        }
                        const n = parseInt(v, 10)
                        if (!isNaN(n) && n >= 0) setGridLeft(String(n))
                      }}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                      %
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      className="h-7 pr-6 text-[10px]"
                      placeholder="right"
                      value={gridRight}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "") {
                          setGridRight("")
                          return
                        }
                        const n = parseInt(v, 10)
                        if (!isNaN(n) && n >= 0) setGridRight(String(n))
                      }}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                      %
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      className="h-7 pr-6 text-[10px]"
                      placeholder="top"
                      value={gridTop}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "") {
                          setGridTop("")
                          return
                        }
                        const n = parseInt(v, 10)
                        if (!isNaN(n) && n >= 0) setGridTop(String(n))
                      }}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                      %
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      className="h-7 pr-6 text-[10px]"
                      placeholder="bottom"
                      value={gridBottom}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "") {
                          setGridBottom("")
                          return
                        }
                        const n = parseInt(v, 10)
                        if (!isNaN(n) && n >= 0) setGridBottom(String(n))
                      }}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                      %
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Show lines and tick marks (X axis)</Label>
                <Select value={xAxisTickMode} onValueChange={(v) => setXAxisTickMode(v as typeof xAxisTickMode)}>
                  <SelectTrigger className="h-7 text-[10px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hide" className="text-xs">
                      Hide
                    </SelectItem>
                    <SelectItem value="show" className="text-xs">
                      Show
                    </SelectItem>
                    <SelectItem value="compact" className="text-xs">
                      Compact
                    </SelectItem>
                    <SelectItem value="rotate45" className="text-xs">
                      Rotate 45°
                    </SelectItem>
                    <SelectItem value="rotate90" className="text-xs">
                      Rotate 90°
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Label</Label>
                <Input
                  className="h-7 text-[10px]"
                  placeholder="X axis name"
                  value={xAxisName}
                  onChange={(e) => setXAxisName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="mb-0.5 block text-[10px]">X label size</Label>
                  <Input
                    type="number"
                    className="h-7 text-[10px]"
                    value={xAxisLabelFontSize}
                    onChange={(e) => setXAxisLabelFontSize(Number(e.target.value) || 12)}
                  />
                </div>
                <div>
                  <Label className="mb-0.5 block text-[10px]">X label color</Label>
                  <ColorInput value={xAxisLabelColor} onChange={setXAxisLabelColor} />
                </div>
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Y axis name</Label>
                <Input className="h-7 text-[10px]" value={yAxisName} onChange={(e) => setYAxisName(e.target.value)} />
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Show lines and tick marks (Y axis)</Label>
                <Select value={yAxisTickMode} onValueChange={(v) => setYAxisTickMode(v as typeof yAxisTickMode)}>
                  <SelectTrigger className="h-7 text-[10px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hide" className="text-xs">
                      Hide
                    </SelectItem>
                    <SelectItem value="show" className="text-xs">
                      Show
                    </SelectItem>
                    <SelectItem value="compact" className="text-xs">
                      Compact
                    </SelectItem>
                    <SelectItem value="rotate45" className="text-xs">
                      Rotate 45°
                    </SelectItem>
                    <SelectItem value="rotate90" className="text-xs">
                      Rotate 90°
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <Label className="mb-0.5 block text-[10px]">Y label size</Label>
                  <Input
                    type="number"
                    className="h-7 text-[10px]"
                    value={yAxisLabelFontSize}
                    onChange={(e) => setYAxisLabelFontSize(Number(e.target.value) || 12)}
                  />
                </div>
                <div>
                  <Label className="mb-0.5 block text-[10px]">Y label color</Label>
                  <ColorInput value={yAxisLabelColor} onChange={setYAxisLabelColor} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px]">Split line (grid)</Label>
                <Switch checked={chartShowGrid} onCheckedChange={setChartShowGrid} />
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Axis line color</Label>
                <ColorInput placeholder="axis" value={axisLineColor} onChange={setAxisLineColor} />
              </div>
              <div>
                <Label className="mb-0.5 block text-[10px]">Split line color</Label>
                <ColorInput placeholder="split" value={splitLineColor} onChange={setSplitLineColor} />
              </div>
            </TabsContent>

            {/* Series – type-specific */}
            <TabsContent value="series" className="m-0 space-y-3">
              {/* Common */}
              {chartType !== "pie" && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Cumulative (stack)</Label>
                    <Switch checked={chartCumulative} onCheckedChange={setChartCumulative} />
                  </div>
                </>
              )}

              {chartType === "bar" && (
                <>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar color</Label>
                    <ColorInput placeholder="#5470c6" value={barColor} onChange={setBarColor} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar width</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min={0}
                        className="h-7 pr-6 text-[10px]"
                        placeholder="60"
                        value={barWidth}
                        onChange={(e) => setBarWidth(e.target.value.replace(/%/g, ""))}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar gap</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min={0}
                        className="h-7 pr-6 text-[10px]"
                        placeholder="30"
                        value={barGap}
                        onChange={(e) => setBarGap(e.target.value.replace(/%/g, ""))}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar border color</Label>
                    <ColorInput value={barBorderColor} onChange={setBarBorderColor} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar border width</Label>
                    <Input
                      type="number"
                      className="h-7 text-[10px]"
                      value={barBorderWidth}
                      onChange={(e) => setBarBorderWidth(Number(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Bar borderRadius (number or 0)</Label>
                    <Input
                      className="h-7 text-[10px]"
                      placeholder="0 or 4"
                      value={String(barBorderRadius)}
                      onChange={(e) => setBarBorderRadius(Number(e.target.value) || 0)}
                    />
                  </div>
                </>
              )}

              {chartType === "line" && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Smooth</Label>
                    <Switch checked={lineSmooth} onCheckedChange={setLineSmooth} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Show symbol</Label>
                    <Switch checked={lineShowSymbol} onCheckedChange={setLineShowSymbol} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Symbol size</Label>
                    <Input
                      type="number"
                      className="h-7 text-[10px]"
                      value={lineSymbolSize}
                      onChange={(e) => setLineSymbolSize(Number(e.target.value) || 4)}
                    />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Line width</Label>
                    <Input
                      type="number"
                      className="h-7 text-[10px]"
                      value={lineWidth}
                      onChange={(e) => setLineWidth(Number(e.target.value) || 2)}
                    />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Line color</Label>
                    <ColorInput placeholder="#5470c6" value={lineColor} onChange={setLineColor} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Area under line</Label>
                    <Switch checked={lineAreaShow} onCheckedChange={setLineAreaShow} />
                  </div>
                  {lineAreaShow && (
                    <div>
                      <Label className="mb-0.5 block text-[10px]">Area opacity (0–1)</Label>
                      <Input
                        type="number"
                        step={0.1}
                        className="h-7 text-[10px]"
                        value={lineAreaOpacity}
                        onChange={(e) => setLineAreaOpacity(Number(e.target.value) || 0.3)}
                      />
                    </div>
                  )}
                </>
              )}

              {chartType === "pie" && (
                <>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Radius inner (0% = full pie)</Label>
                    <div className="relative">
                      <Input
                        className="h-7 pr-6 text-[10px]"
                        placeholder="0"
                        value={pieRadiusInner.replace(/%/g, "")}
                        onChange={(e) => {
                          const v = e.target.value.replace(/%/g, "")
                          setPieRadiusInner(v ? `${v}%` : "0%")
                        }}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Radius outer</Label>
                    <div className="relative">
                      <Input
                        className="h-7 pr-6 text-[10px]"
                        placeholder="75"
                        value={pieRadiusOuter.replace(/%/g, "")}
                        onChange={(e) => {
                          const v = e.target.value.replace(/%/g, "")
                          setPieRadiusOuter(v ? `${v}%` : "75%")
                        }}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <Label className="mb-0.5 block text-[10px]">Center X</Label>
                      <div className="relative">
                        <Input
                          className="h-7 pr-6 text-[10px]"
                          placeholder="50"
                          value={pieCenterX.replace(/%/g, "")}
                          onChange={(e) => {
                            const v = e.target.value.replace(/%/g, "")
                            setPieCenterX(v ? `${v}%` : "50%")
                          }}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                          %
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="mb-0.5 block text-[10px]">Center Y</Label>
                      <div className="relative">
                        <Input
                          className="h-7 pr-6 text-[10px]"
                          placeholder="50"
                          value={pieCenterY.replace(/%/g, "")}
                          onChange={(e) => {
                            const v = e.target.value.replace(/%/g, "")
                            setPieCenterY(v ? `${v}%` : "50%")
                          }}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 dark:text-gray-400">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Rose type</Label>
                    <Select
                      value={pieRoseType || "__none__"}
                      onValueChange={(v) => setPieRoseType(v === "__none__" ? "" : (v as "radius" | "area"))}
                    >
                      <SelectTrigger className="h-7 text-[10px]">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" className="text-xs">
                          None
                        </SelectItem>
                        <SelectItem value="radius" className="text-xs">
                          Radius
                        </SelectItem>
                        <SelectItem value="area" className="text-xs">
                          Area
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px]">Label line</Label>
                    <Switch checked={pieLabelLineShow} onCheckedChange={setPieLabelLineShow} />
                  </div>
                  {pieSliceNames.length > 0 && (
                    <div>
                      <Label className="mb-1 block text-[10px]">Slice colors</Label>
                      <div className="max-h-32 space-y-1 overflow-y-auto">
                        {pieSliceNames.map((name) => (
                          <div key={name} className="flex items-center gap-2">
                            <ColorInput
                              className="shrink-0"
                              swatchOnly
                              value={pieSliceColors[name] ?? ""}
                              onChange={(c) => setPieSliceColors((prev) => ({ ...prev, [name]: c }))}
                            />
                            <span className="min-w-0 truncate text-[10px] text-gray-600 dark:text-gray-400">
                              {name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Item border color</Label>
                    <ColorInput value={pieItemBorderColor} onChange={setPieItemBorderColor} />
                  </div>
                  <div>
                    <Label className="mb-0.5 block text-[10px]">Item border width</Label>
                    <Input
                      type="number"
                      className="h-7 text-[10px]"
                      value={pieItemBorderWidth}
                      onChange={(e) => setPieItemBorderWidth(Number(e.target.value) || 1)}
                    />
                  </div>
                </>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}
